import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { branding } from '../constants/branding';
import { useAppSelector } from '../store/hooks';
import axiosInstance from '../services/axiosConfig';

interface MultiSignatureWallet {
  id: number;
  walletAddress: string;
  blockchain: string;
  requiredSignatures: number;
  totalSignatories: number;
  walletType: string;
  status: string;
  balance: number;
  currency: string;
  escrowTerms: string;
  releaseConditions: string;
  createdAt: string;
  updatedAt: string;
  propertyAsset?: {
    id: number;
    tokenSymbol: string;
    assetName: string;
    assetValue: number;
    assetLocation?: string;
    certificateNumber?: string;
    status?: string;
    description?: string;
  };
  signatories?: MultiSignatureSignatory[];
}

interface MultiSignatureSignatory {
  id: number;
  userId: number;
  username: string;
  signatoryType: string;
  publicKey: string;
  signatoryAddress?: string;
  signatureWeight: number;
  isActive: boolean;
  addedAt: string;
  lastSignatureAt?: string;
}

interface MultiSignatureTransaction {
  id: number;
  walletId: number;
  transactionHash: string;
  transactionType: string;
  amount: number;
  currency: string;
  recipientAddress: string;
  description: string;
  status: string;
  requiredSignatures: number;
  receivedSignatures: number;
  createdAt: string;
  expiresAt: string;
}

const MultiSignatureWallets: React.FC = () => {
  const navigate = useNavigate();
  const token = useAppSelector(state => state.auth.token);
  const user = useAppSelector(state => state.auth.user);
  
  const [wallets, setWallets] = useState<MultiSignatureWallet[]>([]);
  const [pendingTransactions, setPendingTransactions] = useState<MultiSignatureTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedWallet, setSelectedWallet] = useState<MultiSignatureWallet | null>(null);
  const [showWalletDetails, setShowWalletDetails] = useState(false);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<MultiSignatureTransaction | null>(null);
  const [showCreateTransactionModal, setShowCreateTransactionModal] = useState(false);
  const [selectedWalletForTransaction, setSelectedWalletForTransaction] = useState<MultiSignatureWallet | null>(null);
  const [transactionForm, setTransactionForm] = useState({
    amount: '',
    recipientAddress: '',
    description: '',
    transactionType: 'TRANSFER'
  });
  const [isCreatingTransaction, setIsCreatingTransaction] = useState(false);
  const [transactionPermissions, setTransactionPermissions] = useState<{
    userRole: string;
    availableTransactionTypes: string[];
    transactionDescriptions: Record<string, string>;
  } | null>(null);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return '#28a745';
      case 'PENDING': return '#ffc107';
      case 'FROZEN': return '#dc3545';
      case 'CLOSED': return '#6c757d';
      default: return '#6c757d';
    }
  };

  useEffect(() => {
    if (token) {
      loadUserMultiSignatureWallets();
      loadPendingTransactions();
    }
  }, [token]);

  const loadUserMultiSignatureWallets = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/retail/multi-signature-wallets/my-wallets');
      
      if (response.status === 200) {
        const data = response.data;
        if (data.success || data.status === 'success') {
          setWallets(data.data || []);
        } else {
          setError('Failed to load multi-signature wallets');
        }
      }
    } catch (error) {
      console.error('Error loading multi-signature wallets:', error);
      setError('Failed to load multi-signature wallets');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingTransactions = async () => {
    try {
      const response = await axiosInstance.get('/retail/multi-signature-wallets/pending-transactions');
      
      if (response.status === 200) {
        const data = response.data;
        if (data.success || data.status === 'success') {
          setPendingTransactions(data.data || []);
        }
      }
    } catch (error) {
      console.error('Error loading pending transactions:', error);
    }
  };

  const loadTransactionPermissions = async (walletId: number) => {
    try {
      const response = await axiosInstance.get(`/retail/multi-signature-wallets/wallets/${walletId}/transaction-permissions`);
      
      if (response.status === 200) {
        const data = response.data;
        if (data.success || data.status === 'success') {
          setTransactionPermissions(data.data);
        }
      }
    } catch (error) {
      console.error('Error loading transaction permissions:', error);
      setTransactionPermissions(null);
    }
  };

  const handleApproveTransaction = async (transactionId: number) => {
    try {
      const response = await axiosInstance.post(`/retail/multi-signature-wallets/transactions/${transactionId}/approve`, {
        signatoryUserId: user?.id,
        approvalType: 'APPROVE',
        approvalNote: 'Approved by user',
        ipAddress: '127.0.0.1', // In real app, get actual IP
        deviceFingerprint: 'web-browser' // In real app, get device fingerprint
      });

      if (response.status === 200) {
        alert('Transaction approved successfully!');
        loadPendingTransactions(); // Refresh pending transactions
      }
    } catch (error) {
      console.error('Error approving transaction:', error);
      alert('Failed to approve transaction');
    }
  };

  const handleRejectTransaction = async (transactionId: number) => {
    try {
      const response = await axiosInstance.post(`/retail/multi-signature-wallets/transactions/${transactionId}/approve`, {
        signatoryUserId: user?.id,
        approvalType: 'REJECT',
        approvalNote: 'Rejected by user',
        ipAddress: '127.0.0.1',
        deviceFingerprint: 'web-browser'
      });

      if (response.status === 200) {
        alert('Transaction rejected successfully!');
        loadPendingTransactions(); // Refresh pending transactions
      }
    } catch (error) {
      console.error('Error rejecting transaction:', error);
      alert('Failed to reject transaction');
    }
  };

  const handleCreateTransaction = async () => {
    if (!selectedWalletForTransaction || !transactionForm.amount || !transactionForm.recipientAddress || !transactionForm.description) {
      alert('Please fill in all required fields');
      return;
    }

    setIsCreatingTransaction(true);
    try {
      const response = await axiosInstance.post(`/retail/multi-signature-wallets/wallets/${selectedWalletForTransaction.id}/transactions`, {
        amount: parseFloat(transactionForm.amount),
        recipientAddress: transactionForm.recipientAddress,
        description: transactionForm.description,
        transactionType: transactionForm.transactionType,
        currency: selectedWalletForTransaction.currency || 'USD'
      });

      if (response.status === 200) {
        alert('Transaction created successfully! It will be sent to all signatories for approval.');
        setShowCreateTransactionModal(false);
        setSelectedWalletForTransaction(null);
        setTransactionForm({
          amount: '',
          recipientAddress: '',
          description: '',
          transactionType: 'TRANSFER'
        });
        // Refresh pending transactions
        loadPendingTransactions();
      }
    } catch (error) {
      console.error('Error creating transaction:', error);
      alert('Failed to create transaction. You may not have permission to create transactions for this wallet.');
    } finally {
      setIsCreatingTransaction(false);
    }
  };

  const getTransactionStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return '#ffc107';
      case 'APPROVED': return '#28a745';
      case 'EXECUTED': return '#007bff';
      case 'REJECTED': return '#dc3545';
      case 'CANCELLED': return '#6c757d';
      default: return '#6c757d';
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        flexDirection: 'column'
      }}>
        <div className="loading-spinner"></div>
        <p style={{ marginTop: '16px', color: '#666' }}>Loading multi-signature wallets...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '32px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h1 style={{ 
            margin: 0, 
            fontSize: '28px', 
            fontWeight: '700', 
            color: '#1f2937',
            marginBottom: '8px'
          }}>
            Multi-Signature Wallets
          </h1>
          <p style={{ 
            margin: 0, 
            color: '#6b7280', 
            fontSize: '16px' 
          }}>
            Manage your multi-signature wallet participations and pending approvals
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            padding: '12px 24px',
            background: '#f3f4f6',
            color: '#374151',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          ← Back to Dashboard
        </button>
      </div>

      {error && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          color: '#dc2626',
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '24px'
        }}>
          {error}
        </div>
      )}

      {/* Pending Transactions Section */}
      {pendingTransactions.length > 0 && (
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <h2 style={{ 
            margin: '0 0 20px 0', 
            fontSize: '20px', 
            fontWeight: '600',
            color: '#1f2937'
          }}>
            ⚠️ Pending Approvals ({pendingTransactions.length})
          </h2>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {pendingTransactions.map((transaction) => (
              <div key={transaction.id} style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '16px',
                background: '#fef7cd',
                borderLeft: '4px solid #f59e0b'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                      {transaction.description}
                    </h3>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '14px', color: '#6b7280' }}>
                      <span>Amount: ${transaction.amount.toLocaleString()}</span>
                      <span>Type: {transaction.transactionType}</span>
                      <span>Signatures: {transaction.receivedSignatures}/{transaction.requiredSignatures}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleApproveTransaction(transaction.id)}
                      style={{
                        padding: '8px 16px',
                        background: '#28a745',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      ✅ Approve
                    </button>
                    <button
                      onClick={() => handleRejectTransaction(transaction.id)}
                      style={{
                        padding: '8px 16px',
                        background: '#dc3545',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      ❌ Reject
                    </button>
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  Created: {new Date(transaction.createdAt).toLocaleDateString()}
                  {transaction.expiresAt && (
                    <span style={{ marginLeft: '16px' }}>
                      Expires: {new Date(transaction.expiresAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Multi-Signature Wallets Section */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb'
      }}>
        <h2 style={{ 
          margin: '0 0 20px 0', 
          fontSize: '20px', 
          fontWeight: '600',
          color: '#1f2937'
        }}>
          🔐 My Multi-Signature Wallets ({wallets.length})
        </h2>

        {wallets.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280',
            background: '#f9fafb',
            borderRadius: '8px',
            border: '1px dashed #d1d5db'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔐</div>
            <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No Multi-Signature Wallets</h3>
            <p style={{ margin: 0, fontSize: '14px' }}>
              You are not a signatory for any multi-signature wallets yet.
              Contact an administrator to be added as a signatory.
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '16px' }}>
            {wallets.map((wallet) => (
              <div key={wallet.id} style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '20px',
                background: '#f9fafb',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }} onClick={() => {
                setSelectedWallet(wallet);
                setShowWalletDetails(true);
              }}>
                {/* Asset Information Section */}
                {wallet.propertyAsset && (
                  <div style={{
                    background: '#ffffff',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '16px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div>
                        <h4 style={{ 
                          margin: '0 0 8px 0', 
                          fontSize: '18px', 
                          fontWeight: '700', 
                          color: '#1f2937',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <span style={{ 
                            backgroundColor: '#4ecdc4', 
                            color: '#ffffff', 
                            padding: '4px 8px', 
                            borderRadius: '6px', 
                            fontSize: '12px',
                            fontWeight: '600'
                          }}>
                            {wallet.propertyAsset.tokenSymbol}
                          </span>
                          {wallet.propertyAsset.assetName}
                        </h4>
                        <div style={{ 
                          fontSize: '24px', 
                          fontWeight: '700', 
                          color: '#059669',
                          marginBottom: '8px'
                        }}>
                          ${wallet.propertyAsset.assetValue.toLocaleString()}
                        </div>
                        <div style={{ fontSize: '14px', color: '#6b7280' }}>
                          Asset Value
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ 
                          fontSize: '16px', 
                          fontWeight: '600', 
                          color: '#1f2937',
                          marginBottom: '4px'
                        }}>
                          ${wallet.balance?.toLocaleString() || '0'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          Wallet Balance
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Wallet Information Section */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <h3 style={{ 
                      margin: '0 0 8px 0', 
                      fontSize: '16px', 
                      fontWeight: '600', 
                      color: '#1f2937',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      {wallet.propertyAsset ? 'Multi-Signature Escrow' : 'Multi-Signature Wallet'}
                      <span style={{
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '500',
                        textTransform: 'uppercase'
                      }}>
                        {wallet.walletType}
                      </span>
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px', color: '#6b7280' }}>
                      <div>
                        <span style={{ fontWeight: '600', color: '#374151' }}>Blockchain:</span> {wallet.blockchain}
                      </div>
                      <div>
                        <span style={{ fontWeight: '600', color: '#374151' }}>Signatures:</span> {wallet.requiredSignatures}/{wallet.totalSignatories}
                      </div>
                      <div>
                        <span style={{ fontWeight: '600', color: '#374151' }}>Currency:</span> {wallet.currency}
                      </div>
                      <div>
                        <span style={{ fontWeight: '600', color: '#374151' }}>Created:</span> {new Date(wallet.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <span style={{
                    backgroundColor: getStatusColor(wallet.status),
                    color: '#ffffff',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase'
                  }}>
                    {wallet.status}
                  </span>
                </div>
                
                <div style={{ 
                  fontSize: '12px', 
                  color: '#6b7280',
                  fontFamily: 'monospace',
                  backgroundColor: '#f3f4f6',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  marginBottom: '12px'
                }}>
                  Address: {wallet.walletAddress.substring(0, 8)}...{wallet.walletAddress.substring(wallet.walletAddress.length - 6)}
                </div>
                
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      setSelectedWalletForTransaction(wallet);
                      await loadTransactionPermissions(wallet.id);
                      setShowCreateTransactionModal(true);
                    }}
                    style={{
                      padding: '8px 16px',
                      background: '#007bff',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    ➕ Create Transaction
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Wallet Details Modal */}
      {showWalletDetails && selectedWallet && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Wallet Details</h2>
              <button
                onClick={() => setShowWalletDetails(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ display: 'grid', gap: '16px' }}>
              {/* Asset Information Section */}
              {selectedWallet.propertyAsset && (
                <div style={{
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  padding: '20px',
                  border: '1px solid #e5e7eb'
                }}>
                  <h3 style={{ 
                    margin: '0 0 16px 0', 
                    fontSize: '18px', 
                    fontWeight: '600',
                    color: '#1f2937',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    🏠 Associated Property Asset
                    <span style={{
                      backgroundColor: '#4ecdc4',
                      color: '#ffffff',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {selectedWallet.propertyAsset.tokenSymbol}
                    </span>
                  </h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                        Asset Name
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                        {selectedWallet.propertyAsset.assetName}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                        Asset Value
                      </div>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#059669' }}>
                        ${selectedWallet.propertyAsset.assetValue.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                        Token Symbol
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                        {selectedWallet.propertyAsset.tokenSymbol}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                        Asset ID
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                        {selectedWallet.propertyAsset.id}
                      </div>
                    </div>
                    {selectedWallet.propertyAsset.assetLocation && (
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                          Location
                        </div>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                          {selectedWallet.propertyAsset.assetLocation}
                        </div>
                      </div>
                    )}
                    {selectedWallet.propertyAsset.certificateNumber && (
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                          Certificate
                        </div>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                          {selectedWallet.propertyAsset.certificateNumber}
                        </div>
                      </div>
                    )}
                    {selectedWallet.propertyAsset.status && (
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                          Status
                        </div>
                        <div style={{ 
                          fontSize: '16px', 
                          fontWeight: '600', 
                          color: selectedWallet.propertyAsset.status === 'ACTIVE' ? '#059669' : '#dc2626'
                        }}>
                          {selectedWallet.propertyAsset.status}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {selectedWallet.propertyAsset.description && (
                    <div style={{ 
                      marginTop: '16px', 
                      padding: '16px', 
                      background: '#f8f9fa', 
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                        Description
                      </div>
                      <div style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.5' }}>
                        {selectedWallet.propertyAsset.description}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Ownership & Stakeholders Section */}
              {selectedWallet.signatories && selectedWallet.signatories.length > 0 && (
                <div style={{
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  padding: '20px',
                  border: '1px solid #e5e7eb'
                }}>
                  <h3 style={{ 
                    margin: '0 0 16px 0', 
                    fontSize: '18px', 
                    fontWeight: '600',
                    color: '#1f2937',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    👥 Ownership & Stakeholders
                  </h3>
                  
                  {/* Ownership Summary */}
                  <div style={{ 
                    background: '#e3f2fd', 
                    padding: '16px', 
                    borderRadius: '8px', 
                    marginBottom: '16px',
                    border: '1px solid #2196f3'
                  }}>
                    <div style={{ fontWeight: '600', color: '#1976d2', marginBottom: '8px' }}>
                      📋 Ownership Summary
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', fontSize: '14px' }}>
                      <div><strong>Property Owner:</strong> {selectedWallet.signatories.find(s => s.signatoryType === 'PROPERTY_OWNER')?.username || 'Not assigned'}</div>
                      <div><strong>Buyer:</strong> {selectedWallet.signatories.find(s => s.signatoryType === 'BUYER')?.username || 'Not assigned'}</div>
                      <div><strong>Admin:</strong> {selectedWallet.signatories.find(s => s.signatoryType === 'ADMIN')?.username || 'Not assigned'}</div>
                      <div><strong>Total Stakeholders:</strong> {selectedWallet.signatories.length}</div>
                    </div>
                  </div>
                  
                  {/* Individual Stakeholder Cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                    {/* Property Owner */}
                    {selectedWallet.signatories.find(s => s.signatoryType === 'PROPERTY_OWNER') && (
                      <div style={{ 
                        background: '#ffffff', 
                        padding: '16px', 
                        borderRadius: '8px',
                        border: '2px solid #28a745',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        <div style={{ fontWeight: '600', color: '#28a745', marginBottom: '8px' }}>
                          🏠 Property Owner
                        </div>
                        <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                          <strong>Name:</strong> {selectedWallet.signatories.find(s => s.signatoryType === 'PROPERTY_OWNER')?.username}
                        </div>
                        <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                          <strong>Role:</strong> Property Owner
                        </div>
                        <div style={{ fontSize: '14px' }}>
                          <strong>Status:</strong> 
                          <span style={{ 
                            color: selectedWallet.signatories.find(s => s.signatoryType === 'PROPERTY_OWNER')?.isActive ? '#28a745' : '#dc3545',
                            fontWeight: '600',
                            marginLeft: '4px'
                          }}>
                            {selectedWallet.signatories.find(s => s.signatoryType === 'PROPERTY_OWNER')?.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Buyer */}
                    {selectedWallet.signatories.find(s => s.signatoryType === 'BUYER') && (
                      <div style={{ 
                        background: '#ffffff', 
                        padding: '16px', 
                        borderRadius: '8px',
                        border: '2px solid #007bff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        <div style={{ fontWeight: '600', color: '#007bff', marginBottom: '8px' }}>
                          👤 Buyer
                        </div>
                        <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                          <strong>Name:</strong> {selectedWallet.signatories.find(s => s.signatoryType === 'BUYER')?.username}
                        </div>
                        <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                          <strong>Role:</strong> Buyer
                        </div>
                        <div style={{ fontSize: '14px' }}>
                          <strong>Status:</strong> 
                          <span style={{ 
                            color: selectedWallet.signatories.find(s => s.signatoryType === 'BUYER')?.isActive ? '#28a745' : '#dc3545',
                            fontWeight: '600',
                            marginLeft: '4px'
                          }}>
                            {selectedWallet.signatories.find(s => s.signatoryType === 'BUYER')?.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Admin */}
                    {selectedWallet.signatories.find(s => s.signatoryType === 'ADMIN') && (
                      <div style={{ 
                        background: '#ffffff', 
                        padding: '16px', 
                        borderRadius: '8px',
                        border: '2px solid #6c757d',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        <div style={{ fontWeight: '600', color: '#6c757d', marginBottom: '8px' }}>
                          ⚙️ Admin
                        </div>
                        <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                          <strong>Name:</strong> {selectedWallet.signatories.find(s => s.signatoryType === 'ADMIN')?.username}
                        </div>
                        <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                          <strong>Role:</strong> Administrator
                        </div>
                        <div style={{ fontSize: '14px' }}>
                          <strong>Status:</strong> 
                          <span style={{ 
                            color: selectedWallet.signatories.find(s => s.signatoryType === 'ADMIN')?.isActive ? '#28a745' : '#dc3545',
                            fontWeight: '600',
                            marginLeft: '4px'
                          }}>
                            {selectedWallet.signatories.find(s => s.signatoryType === 'ADMIN')?.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Other Signatories */}
                    {selectedWallet.signatories.filter(s => !['PROPERTY_OWNER', 'BUYER', 'ADMIN'].includes(s.signatoryType)).map((signatory) => (
                      <div key={signatory.id} style={{ 
                        background: '#ffffff', 
                        padding: '16px', 
                        borderRadius: '8px',
                        border: '2px solid #ffc107',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        <div style={{ fontWeight: '600', color: '#ffc107', marginBottom: '8px' }}>
                          👥 {signatory.signatoryType.replace('_', ' ')}
                        </div>
                        <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                          <strong>Name:</strong> {signatory.username}
                        </div>
                        <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                          <strong>Role:</strong> {signatory.signatoryType.replace('_', ' ')}
                        </div>
                        <div style={{ fontSize: '14px' }}>
                          <strong>Status:</strong> 
                          <span style={{ 
                            color: signatory.isActive ? '#28a745' : '#dc3545',
                            fontWeight: '600',
                            marginLeft: '4px'
                          }}>
                            {signatory.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Escrow Terms & Conditions */}
              {(selectedWallet.escrowTerms || selectedWallet.releaseConditions) && (
                <div style={{
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  padding: '20px',
                  border: '1px solid #e5e7eb'
                }}>
                  <h3 style={{ 
                    margin: '0 0 16px 0', 
                    fontSize: '18px', 
                    fontWeight: '600',
                    color: '#1f2937',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    ⚖️ Escrow Terms & Conditions
                  </h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    {selectedWallet.escrowTerms && (
                      <div style={{ 
                        background: '#fff3cd', 
                        padding: '16px', 
                        borderRadius: '8px',
                        border: '1px solid #ffeaa7'
                      }}>
                        <div style={{ fontWeight: '600', color: '#856404', marginBottom: '8px' }}>
                          📋 Escrow Terms
                        </div>
                        <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                          {selectedWallet.escrowTerms}
                        </div>
                      </div>
                    )}
                    
                    {selectedWallet.releaseConditions && (
                      <div style={{ 
                        background: '#d1ecf1', 
                        padding: '16px', 
                        borderRadius: '8px',
                        border: '1px solid #bee5eb'
                      }}>
                        <div style={{ fontWeight: '600', color: '#0c5460', marginBottom: '8px' }}>
                          🔓 Release Conditions
                        </div>
                        <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                          {selectedWallet.releaseConditions}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Wallet Information Section */}
              <div style={{
                background: '#f8f9fa',
                borderRadius: '8px',
                padding: '20px',
                border: '1px solid #e5e7eb'
              }}>
                <h3 style={{ 
                  margin: '0 0 16px 0', 
                  fontSize: '18px', 
                  fontWeight: '600',
                  color: '#1f2937',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  🔐 Wallet Information
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Status
                    </div>
                    <div style={{ 
                      fontSize: '16px', 
                      fontWeight: '600',
                      color: getStatusColor(selectedWallet.status)
                    }}>
                      {selectedWallet.status}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Blockchain
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                      {selectedWallet.blockchain}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Required Signatures
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                      {selectedWallet.requiredSignatures}/{selectedWallet.totalSignatories}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Wallet Balance
                    </div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937' }}>
                      ${selectedWallet.balance?.toLocaleString() || '0'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Currency
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                      {selectedWallet.currency}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Wallet Type
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                      {selectedWallet.walletType}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Created Date
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                      {new Date(selectedWallet.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Last Updated
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                      {new Date(selectedWallet.updatedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                  </div>
                </div>
                
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                    Wallet Address
                  </div>
                  <div style={{ 
                    fontFamily: 'monospace', 
                    fontSize: '14px', 
                    background: '#ffffff', 
                    padding: '12px', 
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb',
                    wordBreak: 'break-all'
                  }}>
                    {selectedWallet.walletAddress}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Transaction Modal */}
      {showCreateTransactionModal && selectedWalletForTransaction && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Create Transaction</h2>
              <button
                onClick={() => setShowCreateTransactionModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <strong>Wallet:</strong> {selectedWalletForTransaction.walletAddress.substring(0, 8)}...{selectedWalletForTransaction.walletAddress.substring(selectedWalletForTransaction.walletAddress.length - 6)}
            </div>
            
            {transactionPermissions && (
              <div style={{ 
                background: '#e3f2fd', 
                padding: '12px', 
                borderRadius: '6px', 
                marginBottom: '16px',
                border: '1px solid #2196f3'
              }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#1976d2', marginBottom: '4px' }}>
                  Your Role: {transactionPermissions.userRole.replace('_', ' ')}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  You can initiate the following transaction types:
                </div>
              </div>
            )}
            
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Amount *
                </label>
                <input
                  type="number"
                  value={transactionForm.amount}
                  onChange={(e) => setTransactionForm({...transactionForm, amount: e.target.value})}
                  placeholder="Enter amount"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Recipient Address *
                </label>
                <input
                  type="text"
                  value={transactionForm.recipientAddress}
                  onChange={(e) => setTransactionForm({...transactionForm, recipientAddress: e.target.value})}
                  placeholder="Enter recipient wallet address"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'monospace'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Description *
                </label>
                <input
                  type="text"
                  value={transactionForm.description}
                  onChange={(e) => setTransactionForm({...transactionForm, description: e.target.value})}
                  placeholder="Enter transaction description"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Transaction Type
                </label>
                <select
                  value={transactionForm.transactionType}
                  onChange={(e) => setTransactionForm({...transactionForm, transactionType: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  {transactionPermissions ? (
                    transactionPermissions.availableTransactionTypes.map(type => (
                      <option key={type} value={type}>
                        {transactionPermissions.transactionDescriptions[type] || type}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="TRANSFER">Transfer</option>
                      <option value="PAYMENT">Payment</option>
                      <option value="WITHDRAWAL">Withdrawal</option>
                    </>
                  )}
                </select>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={handleCreateTransaction}
                disabled={isCreatingTransaction}
                style={{
                  padding: '12px 24px',
                  background: isCreatingTransaction ? '#6c757d' : '#007bff',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isCreatingTransaction ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  flex: 1
                }}
              >
                {isCreatingTransaction ? 'Creating...' : 'Create Transaction'}
              </button>
              <button
                onClick={() => setShowCreateTransactionModal(false)}
                style={{
                  padding: '12px 24px',
                  background: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSignatureWallets; 